import React from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Typography,
  Box,
  Tooltip,
  Avatar,
  LinearProgress,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BiLogoAndroid,
  BiLogoApple,
  BiQuestionMark,
  BiSolidCircle,
  BiSolidMoon,
  BiSolidAdjust,
  BiSolidBadgeCheck,
  BiSolidMessageRoundedCheck,
  BiSolidMinusCircle,
  BiSolidXCircle,
  BiSolidHelpCircle,
  BiDotsVerticalRounded,
  BiPowerOff,
  BiRevision,
  BiLockOpen,
} from 'react-icons/bi';
import { PiSteeringWheelFill } from 'react-icons/pi';
import { toast } from 'react-toastify';

import { Drivers } from '@gql/base-types';
import { useTerminateDriverMutation } from '../queries/terminateDriver.mutation.generated';
import { useReinstateDriverMutation } from '../queries/reinstateDriver.mutation.generated';
import { capFirst, getCleansedPhoneNumber } from '@services/formatService';
import { authService } from '@features/auth/services/authService';
import { useUser } from '@store';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import { isPortalRole } from '@features/userManagement/utils/roleUtils';
import RoleChip from '@features/userManagement/components/RoleChip';

type SortKey = 'id' | 'name' | 'role' | 'phone' | 'status' | 'active';
type SortOrder = 'asc' | 'desc';

interface DriversTableProps {
  drivers: Drivers[];
  loading?: boolean;
  refetch?: () => void;
}

const DriversTable: React.FC<DriversTableProps> = ({ drivers, loading, refetch }) => {
  const theme = useTheme();
  const currentUser = useUser();
  const hasUserManagement = useFeaturePermission(FeatureName.USER_MANAGEMENT);
  const [terminateDriver] = useTerminateDriverMutation();
  const [reinstateDriver] = useReinstateDriverMutation();

  const [orderBy, setOrderBy] = React.useState<SortKey>('name');
  const [order, setOrder] = React.useState<SortOrder>('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  // Action menu state
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuDriver, setMenuDriver] = React.useState<Drivers | null>(null);

  const handleSort = (column: SortKey) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedDrivers = React.useMemo(() => {
    return [...drivers].sort((a, b) => {
      let aVal: string | number | boolean = '';
      let bVal: string | number | boolean = '';

      switch (orderBy) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'name':
          aVal = a.user?.display_name?.toLowerCase() || '';
          bVal = b.user?.display_name?.toLowerCase() || '';
          break;
        case 'role':
          aVal = a.user?.default_role?.toLowerCase() || '';
          bVal = b.user?.default_role?.toLowerCase() || '';
          break;
        case 'phone':
          aVal = a.user?.phone || '';
          bVal = b.user?.phone || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'active':
          aVal = a.user?.active ?? false;
          bVal = b.user?.active ?? false;
          break;
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [drivers, orderBy, order]);

  const paginatedDrivers = sortedDrivers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'online':
        return <BiSolidCircle size={16} style={{ color: theme.palette.success.main }} />;
      case 'offline':
        return <BiSolidMoon size={16} style={{ color: theme.palette.grey[400] }} />;
      case 'training':
        return <BiSolidCircle size={16} style={{ color: theme.palette.warning.main }} />;
      case 'onboarding':
        return <BiSolidAdjust size={16} style={{ color: theme.palette.warning.main }} />;
      case 'interviewed':
        return <BiSolidMessageRoundedCheck size={16} style={{ color: theme.palette.info.main }} />;
      case 'new':
        return <BiSolidBadgeCheck size={16} style={{ color: theme.palette.info.main }} />;
      case 'rejected':
        return <BiSolidMinusCircle size={16} style={{ color: theme.palette.error.main }} />;
      case 'terminated':
        return <BiSolidXCircle size={16} style={{ color: theme.palette.error.main }} />;
      default:
        return <BiSolidHelpCircle size={16} style={{ color: theme.palette.grey[400] }} />;
    }
  };

  const getPhoneIcon = (phoneType?: string) => {
    switch (phoneType) {
      case 'android':
        return (
          <Tooltip title="Android">
            <Box display="flex"><BiLogoAndroid size={20} style={{ color: (theme.palette as any).android?.main || theme.palette.success.main }} /></Box>
          </Tooltip>
        );
      case 'ios':
        return (
          <Tooltip title="iOS">
            <Box display="flex"><BiLogoApple size={20} style={{ color: (theme.palette as any).ios?.main || theme.palette.grey[700] }} /></Box>
          </Tooltip>
        );
      default:
        return (
          <Tooltip title="Unknown">
            <Box display="flex"><BiQuestionMark size={20} style={{ color: theme.palette.grey[400] }} /></Box>
          </Tooltip>
        );
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, driver: Drivers) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuAnchor(e.currentTarget);
    setMenuDriver(driver);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuDriver(null);
  };

  const handleResetPassword = async () => {
    if (!menuDriver?.user?.email) return;
    closeMenu();
    try {
      await authService.resetPassword(menuDriver.user.email);
      toast.success(`Password reset email sent to ${menuDriver.user.email}`);
    } catch (err: any) {
      toast.error(`Failed to send password reset: ${err.message}`);
    }
  };

  const isSelf = menuDriver?.user?.id === currentUser?.profile?.id;
  const isComboDriver = menuDriver ? isPortalRole(menuDriver.user?.default_role) : false;
  const canTerminate = menuDriver
    ? ['online', 'offline', 'new', 'onboarding'].includes(menuDriver.status)
      && !isSelf
      && (!isComboDriver || hasUserManagement)
    : false;
  const canReinstate = menuDriver
    ? menuDriver.status === 'terminated'
      && (!isComboDriver || hasUserManagement)
    : false;

  const handleDeactivate = async () => {
    if (!menuDriver) return;
    closeMenu();
    const confirmed = window.confirm(
      `Deactivate ${menuDriver.user?.display_name || 'this driver'}? They will lose access to the app.`
    );
    if (!confirmed) return;
    try {
      const result = await terminateDriver({ variables: { driverId: menuDriver.id } });
      if (result.data?.update_drivers?.affected_rows && result.data?.update_users?.affected_rows) {
        toast.success('Driver deactivated');
        refetch?.();
      } else {
        throw new Error('Failed to deactivate driver');
      }
    } catch (err: any) {
      toast.error(`Failed to deactivate: ${err.message}`);
    }
  };

  const handleReactivate = async () => {
    if (!menuDriver) return;
    closeMenu();
    const confirmed = window.confirm(
      `Reactivate ${menuDriver.user?.display_name || 'this driver'}? Their access will be restored.`
    );
    if (!confirmed) return;
    try {
      const result = await reinstateDriver({ variables: { driverId: menuDriver.id } });
      if (result.data?.update_drivers?.affected_rows && result.data?.update_users?.affected_rows) {
        toast.success('Driver reactivated');
        refetch?.();
      } else {
        throw new Error('Failed to reactivate driver');
      }
    } catch (err: any) {
      toast.error(`Failed to reactivate: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <TableContainer>
        <LinearProgress />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>OS</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={24} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>
                <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                  Driver
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'role'} direction={orderBy === 'role' ? order : 'asc'} onClick={() => handleSort('role')}>
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'phone'} direction={orderBy === 'phone' ? order : 'asc'} onClick={() => handleSort('phone')}>
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell>OS</TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'active'} direction={orderBy === 'active' ? order : 'asc'} onClick={() => handleSort('active')}>
                  Active
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDrivers.map(driver => {
              return (
              <TableRow
                key={driver.id}
                hover
                component={Link}
                to={`/drivers/${driver.id}`}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  height: 48,
                  '&:hover': { backgroundColor: 'rgba(32, 36, 48, 0.02)' },
                  transition: 'background-color 150ms',
                }}
              >
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{driver.id}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5} sx={!driver.user?.active ? { opacity: 0.55 } : undefined}>
                    <Avatar
                      src={driver.user?.avatar_url || undefined}
                      alt={driver.user?.display_name || ''}
                      sx={{ width: 34, height: 34, fontSize: 14 }}
                    >
                      {driver.user?.display_name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }} noWrap>
                          {driver.user?.display_name || 'Unnamed Driver'}
                        </Typography>
                        <Tooltip title={`Driver${driver.status ? ` (${driver.status})` : ''}`}>
                          <Box display="flex" alignItems="center" sx={{ color: theme.palette.secondary.main }}>
                            <PiSteeringWheelFill size={14} />
                          </Box>
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                        {driver.user?.email || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <RoleChip role={driver.user?.default_role || ''} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getCleansedPhoneNumber(driver.user?.phone) || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {getPhoneIcon(driver.phone_type ?? undefined)}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    {getStatusIcon(driver.status)}
                    <Typography variant="body2">{capFirst(driver.status) || '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={driver.user?.active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      bgcolor: driver.user?.active ? '#f3fbf4' : '#f1f2f3',
                      color: driver.user?.active ? '#30b448' : '#5f666d',
                      fontSize: 11,
                    }}
                  />
                </TableCell>
                <TableCell onClick={e => { e.stopPropagation(); e.preventDefault(); }}>
                  <Tooltip title="Actions">
                    <IconButton size="small" onClick={e => openMenu(e, driver)}>
                      <BiDotsVerticalRounded size={18} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}

            {paginatedDrivers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No drivers found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleResetPassword} disabled={!menuDriver?.user?.email}>
          <ListItemIcon><BiLockOpen size={18} /></ListItemIcon>
          <ListItemText>Send reset password email</ListItemText>
        </MenuItem>
        {canTerminate && (
          <MenuItem onClick={handleDeactivate} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'error.main' }}><BiPowerOff size={18} /></ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        )}
        {canReinstate && (
          <MenuItem onClick={handleReactivate} sx={{ color: 'success.main' }}>
            <ListItemIcon sx={{ color: 'success.main' }}><BiRevision size={18} /></ListItemIcon>
            <ListItemText>Reactivate</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {drivers.length > 10 && (
        <TablePagination
          component="div"
          count={drivers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </>
  );
};

export default DriversTable;
